import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

import enCatalog from "../i18n/en.json";
import hiCatalog from "../i18n/hi.json";
import frCatalog from "../i18n/fr.json";
import arCatalog from "../i18n/ar.json";

/**
 * Minimal, dependency-free i18n.
 *
 * Resolution order (highest first):
 *   1. `?lang=` query param
 *   2. localStorage (persisted user choice on this device)
 *   3. logged-in user.locale (from the backend profile)
 *   4. backend detection (Accept-Language) on first /api/locale call
 *   5. DEFAULT_LOCALE ("en")
 *
 * Translation lookup falls back to English everywhere — never break a
 * page because a string is missing in the active catalog.
 *
 * Sidecar DB translations are layered on top via /api/translations
 * (handled at the page/component level, not here).
 */

const CATALOGS = {
  en: enCatalog,
  hi: hiCatalog,
  fr: frCatalog,
  ar: arCatalog,
};

const STORAGE_KEY = "fg_locale";
const COOKIE_NAME = "fg_locale";
const USER_CHOSE_KEY = "fg_locale_user_chose";
const DEFAULT_LOCALE = "en";

const DEFAULT_SUPPORTED = [
  { code: "en", name: "English",  native: "English",  dir: "ltr" },
  { code: "hi", name: "Hindi",    native: "हिन्दी",     dir: "ltr" },
  { code: "fr", name: "French",   native: "Français", dir: "ltr" },
  { code: "ar", name: "Arabic",   native: "العربية",    dir: "rtl" },
];

const I18nContext = createContext({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  supported: DEFAULT_SUPPORTED,
  dir: "ltr",
  t: (key) => key,
});

function lookupDotted(obj, dotted) {
  if (!obj) return undefined;
  return dotted.split(".").reduce(
    (acc, part) => (acc && typeof acc === "object" ? acc[part] : undefined),
    obj,
  );
}

function interpolate(template, vars) {
  if (!template || typeof template !== "string" || !vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, k) =>
    Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : `{{${k}}}`,
  );
}

function readQueryLocale() {
  if (typeof window === "undefined") return null;
  const m = window.location.search.match(/[?&]lang=([a-zA-Z-]{2,8})/);
  return m ? m[1].toLowerCase() : null;
}

function readStoredLocale() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return stored;
  } catch { /* ignore */ }
  const cookieMatch = document.cookie.match(new RegExp(`${COOKIE_NAME}=([^;]+)`));
  if (cookieMatch) return decodeURIComponent(cookieMatch[1]);
  return null;
}

function writeStoredLocale(code) {
  try { localStorage.setItem(STORAGE_KEY, code); } catch { /* ignore */ }
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(code)};path=/;max-age=${60 * 60 * 24 * 365}`;
}

export function I18nProvider({ children }) {
  // useAuth() is safe here because App.js mounts <AuthProvider> outside this.
  const auth = useAuth();
  const user = auth?.user;

  const [locale, setLocaleState] = useState(() => {
    const q = readQueryLocale();
    if (q) return q;
    return readStoredLocale() || DEFAULT_LOCALE;
  });
  const [supported, setSupported] = useState(DEFAULT_SUPPORTED);
  const initFetchedRef = useRef(false);
  const userSyncedRef = useRef(false);

  // One-time: fetch supported-locales catalog from the backend.
  useEffect(() => {
    if (initFetchedRef.current) return;
    initFetchedRef.current = true;
    api.get("/locale")
      .then((r) => {
        if (Array.isArray(r.data?.supported) && r.data.supported.length) {
          setSupported(r.data.supported);
        }
        // If user hasn't picked AND no query/local override, honour the
        // backend detection (Accept-Language).
        const hasOverride = readQueryLocale() || readStoredLocale();
        if (!hasOverride && r.data?.detected) {
          setLocaleState(r.data.detected);
        }
      })
      .catch(() => { /* ignore */ });
  }, []);

  // Once the user is loaded, if they have a saved locale AND this device
  // hasn't been explicitly chosen otherwise, adopt the profile locale.
  useEffect(() => {
    if (userSyncedRef.current) return;
    if (!user || user === false || typeof user !== "object") return;
    userSyncedRef.current = true;
    let userChose = false;
    try { userChose = !!localStorage.getItem(USER_CHOSE_KEY); } catch { /* ignore */ }
    const userLocale = (user.locale || "").trim();
    if (userLocale && !userChose && !readQueryLocale()) {
      setLocaleState(userLocale);
    }
  }, [user]);

  // Persist + flip <html lang>/dir whenever locale changes.
  useEffect(() => {
    writeStoredLocale(locale);
    const meta = supported.find((s) => s.code === locale);
    const dir = meta?.dir || "ltr";
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
      document.documentElement.dir = dir;
    }
  }, [locale, supported]);

  const setLocale = useCallback((next) => {
    if (!next || typeof next !== "string") return;
    setLocaleState(next);
    try { localStorage.setItem(USER_CHOSE_KEY, "1"); } catch { /* ignore */ }
    // Best-effort: persist to profile so other devices follow.
    if (user && user !== false) {
      api.patch("/auth/me", { locale: next }).catch(() => { /* anonymous OK */ });
    }
  }, [user]);

  const t = useCallback(
    (key, vars) => {
      const active = CATALOGS[locale];
      const value = (active && lookupDotted(active, key))
                 ?? lookupDotted(CATALOGS[DEFAULT_LOCALE], key)
                 ?? key;
      return typeof value === "string" ? interpolate(value, vars) : value;
    },
    [locale],
  );

  const dir = useMemo(
    () => supported.find((s) => s.code === locale)?.dir || "ltr",
    [supported, locale],
  );

  const value = useMemo(
    () => ({ locale, setLocale, supported, dir, t }),
    [locale, setLocale, supported, dir, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export function useT() {
  return useContext(I18nContext).t;
}
